import { Helmet } from 'react-helmet-async';
import { SEO_CONSTANTS } from '../../utils/seo';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  // Recipe-specific
  recipe?: {
    prepTime?: string;
    cookTime?: string;
    rating?: number;
    ratingCount?: number;
  };
  // Don't index this page
  noindex?: boolean;
}

/**
 * SEO component for managing meta tags across the site
 * Uses react-helmet-async for dynamic head management
 */
export default function SEO({
  title,
  description,
  image,
  url,
  type = 'website',
  recipe,
  noindex = false,
}: SEOProps) {
  const {
    siteName,
    siteUrl,
    defaultImage,
    twitterHandle,
    locale,
  } = SEO_CONSTANTS;

  // Build full title
  const fullTitle = title ? `${title} | ${siteName}` : siteName;

  // Default description
  const metaDescription = description || 'Discover delicious homemade recipes at Evan\'s Oven. Easy-to-follow recipes for every occasion.';

  // Full URL
  const canonicalUrl = url ? `${siteUrl}${url}` : siteUrl;

  // Image URL
  const ogImage = image || defaultImage;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Recipe-specific meta (for Pinterest, etc.) */}
      {recipe?.prepTime && (
        <meta property="recipe:prep_time" content={recipe.prepTime} />
      )}
      {recipe?.cookTime && (
        <meta property="recipe:cook_time" content={recipe.cookTime} />
      )}
    </Helmet>
  );
}
